/**
 * Photo outbox: durable queue for uploading photos to Supabase Storage +
 * linking the resulting public URL onto a row (append into a text[] column
 * like problem_photos / completion_photos / id_plate_photos).
 *
 * Why this exists:
 *  - A `<input type=file>` produces a File that lives only in memory. A
 *    page reload (including our auto-heal reload path) destroys it. Long
 *    idle → focus refresh → reload has been eating field techs' photos.
 *  - Mobile networks flap. The upload may need to retry silently, not
 *    burden the user with "photo failed, take it again."
 *  - Even if the upload can't happen, the photo needs to survive on the
 *    device until it does.
 *
 * How:
 *  1. Caller writes File to IndexedDB via enqueuePhoto(). Return is
 *     synchronous-fast (IDB put ~5-20ms) so the UI can pop the local
 *     thumbnail immediately.
 *  2. enqueuePhoto then attempts an inline upload while marking a
 *     critical op so AuthProvider can't reload out from under it.
 *  3. Success → outbox entry deleted, publicUrl returned.
 *     Failure → entry stays queued; the sync hook retries on
 *     online / focus / interval.
 *
 * Scope:
 *  - Every "append URL to a text[] column on a specific row" flow: ASV
 *    items, work_order_tasks, work_order_sub_tasks, fca_findings,
 *    task_vendor_bids. If a new link shape appears (e.g. writing to a
 *    row that doesn't have a plain array column), extend LinkStrategy
 *    with a new discriminant.
 */
import { supabase } from '@/lib/supabase/client'
import { beginCriticalOp } from './critical-ops'
import { isAuthError, isNetworkError, isOnline, withTimeout } from './network'

// ─── Types ─────────────────────────────────────────────────────────────

export type LinkableTable =
  | 'asv_items'
  | 'work_order_tasks'
  | 'work_order_sub_tasks'
  | 'fca_findings'
  | 'task_vendor_bids'

/**
 * The "where does the public URL go once storage upload succeeds" strategy.
 * Kept as a plain data object (no functions) so it survives IndexedDB
 * round-trips across page reloads.
 *
 * Discriminated by `kind`:
 *   - array_append: read text[] on `table.rowId`, dedupe, append, write back.
 *     Covers ASV items, FCA findings, WO task problem_photos, sub-task photos,
 *     vendor bid completion_photos-as-plain-array cases.
 *   - wo_task_completion_photo: invokes the crew_add_task_completion_photos
 *     RPC which appends to work_order_tasks.completion_photos AND writes a
 *     wo_completion_photo_uploads provenance row (who/when). One file per call.
 */
export type LinkStrategy =
  | {
      kind: 'array_append'
      table: LinkableTable
      rowId: string
      /** Text[] column to append into. */
      field: string
      /**
       * Optional extra columns to set in the same UPDATE — e.g. stamping
       * work_completed_at when completion photos are attached to a bid.
       * Values must be JSON-serializable so they survive IDB round trips.
       */
      extraSet?: Record<string, string | number | boolean | null>
    }
  | {
      kind: 'wo_task_completion_photo'
      taskId: string
      fileName: string
      contentType: string
      byteSize: number
    }

export interface PhotoOutboxEntry {
  id: string
  bucket: string
  storagePath: string
  fileName: string
  fileType: string
  fileSize: number
  /** The raw bytes. Stored as a Blob so IDB can round-trip it efficiently. */
  blob: Blob
  link: LinkStrategy
  /** Optional label the UI can show ("Front lobby ceiling"). Never sent to server. */
  label?: string | null
  queuedAt: number
  attempts: number
  lastError?: string
  lastAttemptAt?: number
}

// ─── IndexedDB plumbing ────────────────────────────────────────────────

const DB_NAME = 'opsyri-offline'
const DB_VERSION = 1
const STORE = 'photo-outbox'

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'))
      return
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('Failed to open IDB'))
  })
  return dbPromise
}

function txPromise<T = void>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode)
        const store = tx.objectStore(STORE)
        let result: T
        Promise.resolve(fn(store)).then(
          (v) => {
            result = v as T
          },
          (err) => {
            try { tx.abort() } catch { /* ignore */ }
            reject(err)
          },
        )
        tx.oncomplete = () => resolve(result)
        tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'))
        tx.onerror = () => reject(tx.error ?? new Error('IDB transaction error'))
      }),
  )
}

function reqPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IDB request failed'))
  })
}

// ─── Public API ────────────────────────────────────────────────────────

const UPLOAD_TIMEOUT_MS = 30_000
const LINK_TIMEOUT_MS = 12_000

const listeners = new Set<() => void>()
function notify() {
  for (const cb of listeners) {
    try { cb() } catch (err) { console.warn('photo-outbox listener threw', err) }
  }
}

export function subscribePhotoOutbox(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Newest-first list of pending entries. */
export async function listPendingPhotos(): Promise<PhotoOutboxEntry[]> {
  const rows = await txPromise('readonly', async (store) => {
    return reqPromise<PhotoOutboxEntry[]>(store.getAll() as IDBRequest<PhotoOutboxEntry[]>)
  })
  return [...rows].sort((a, b) => b.queuedAt - a.queuedAt)
}

/** Just the count — cheap read for the banner. */
export async function pendingPhotoCount(): Promise<number> {
  return txPromise('readonly', async (store) => {
    return reqPromise<number>(store.count() as IDBRequest<number>)
  })
}

export async function getPhotoBlob(id: string): Promise<Blob | null> {
  const entry = await txPromise('readonly', async (store) => {
    return reqPromise<PhotoOutboxEntry | undefined>(store.get(id) as IDBRequest<PhotoOutboxEntry | undefined>)
  })
  return entry?.blob ?? null
}

export async function removePhoto(id: string): Promise<void> {
  await txPromise('readwrite', async (store) => {
    store.delete(id)
  })
  notify()
}

// ─── Enqueue + upload ──────────────────────────────────────────────────

export interface EnqueuePhotoInput {
  bucket: string
  storagePath: string
  file: File | Blob
  fileName: string
  link: LinkStrategy
  label?: string | null
}

export interface EnqueuePhotoResult {
  /** The entry id — the caller can hold onto this to display a local thumb. */
  id: string
  /** Non-null when the inline upload+link succeeded and the row already has this URL. */
  publicUrl: string | null
  /** Non-null when the upload failed and the entry is queued for later retry. */
  error?: string
}

function newId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  }
}

/**
 * Persist the photo, then try to upload it inline. Whether the upload lands
 * or not, the photo is safely on device — this call ALWAYS resolves.
 */
export async function enqueuePhoto(input: EnqueuePhotoInput): Promise<EnqueuePhotoResult> {
  const id = newId()
  const blob = input.file instanceof Blob ? input.file : new Blob([input.file])
  const entry: PhotoOutboxEntry = {
    id,
    bucket: input.bucket,
    storagePath: input.storagePath,
    fileName: input.fileName,
    fileType: (input.file as File).type || 'application/octet-stream',
    fileSize: blob.size,
    blob,
    link: input.link,
    label: input.label ?? null,
    queuedAt: Date.now(),
    attempts: 0,
  }
  await txPromise('readwrite', async (store) => {
    store.put(entry)
  })
  notify()

  const flushResult = await flushOne(id).catch((err) => ({
    ok: false as const,
    error: err instanceof Error ? err.message : String(err),
  }))
  if (flushResult.ok) {
    return { id, publicUrl: flushResult.publicUrl }
  }
  return { id, publicUrl: null, error: flushResult.error }
}

/**
 * Try to upload + link exactly one entry (by id, or "next pending" if omitted).
 * Success: the entry is removed. Failure: attempts++ and the entry stays.
 */
export async function flushOne(id?: string): Promise<
  | { ok: true; publicUrl: string; entryId: string }
  | { ok: false; error: string; entryId?: string }
> {
  const entry = await getEntry(id)
  if (!entry) return { ok: false, error: 'no pending entry' }
  return runEntry(entry)
}

async function getEntry(id?: string): Promise<PhotoOutboxEntry | null> {
  if (id) {
    return await txPromise('readonly', async (store) => {
      return reqPromise<PhotoOutboxEntry | undefined>(store.get(id) as IDBRequest<PhotoOutboxEntry | undefined>)
    }).then((e) => e ?? null)
  }
  const list = await listPendingPhotos()
  return list[list.length - 1] ?? null // oldest first for FIFO
}

/**
 * Entries queued before the WO task photo path gained its `work_orders/`
 * prefix fail the bucket's INSERT policy (400) forever. Rewrite them.
 */
function normalizeStoragePath(entry: PhotoOutboxEntry): PhotoOutboxEntry {
  if (entry.bucket !== 'work-order-photos' || entry.storagePath.startsWith('work_orders/')) {
    return entry
  }
  return { ...entry, storagePath: `work_orders/${entry.storagePath}` }
}

async function runEntry(
  rawEntry: PhotoOutboxEntry,
): Promise<
  | { ok: true; publicUrl: string; entryId: string }
  | { ok: false; error: string; entryId: string }
> {
  const entry = normalizeStoragePath(rawEntry)
  const endOp = beginCriticalOp()
  try {
    // 1. Upload bytes. `upsert: true` so a retry with the same path (e.g.
    //    the storage upload succeeded but the row link didn't) doesn't
    //    fail with "The resource already exists".
    const uploadPromise = supabase.storage
      .from(entry.bucket)
      .upload(entry.storagePath, entry.blob, {
        contentType: entry.fileType,
        upsert: true,
      })
    const { error: upErr } = await withTimeout(
      Promise.resolve(uploadPromise).then((r) => r),
      UPLOAD_TIMEOUT_MS,
      'photo-upload',
    )
    if (upErr) throw upErr

    // 2. Public URL (deterministic; no network round-trip).
    const { data: urlData } = supabase.storage
      .from(entry.bucket)
      .getPublicUrl(entry.storagePath)
    const publicUrl = urlData.publicUrl

    // 3. Link the URL onto its destination row (append or RPC).
    await linkPhotoToRow(entry.link, publicUrl, entry)

    // 4. Clean up.
    await removePhoto(entry.id)
    return { ok: true, publicUrl, entryId: entry.id }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    await markAttemptFailure(entry.id, message)
    return { ok: false, error: message, entryId: entry.id }
  } finally {
    endOp()
  }
}

async function markAttemptFailure(id: string, error: string): Promise<void> {
  await txPromise('readwrite', async (store) => {
    const current = await reqPromise<PhotoOutboxEntry | undefined>(store.get(id) as IDBRequest<PhotoOutboxEntry | undefined>)
    if (!current) return
    store.put({
      ...current,
      attempts: current.attempts + 1,
      lastError: error,
      lastAttemptAt: Date.now(),
    })
  })
  notify()
}

/** Dispatch to the right link handler based on strategy kind. Idempotent. */
async function linkPhotoToRow(
  link: LinkStrategy,
  publicUrl: string,
  entry: PhotoOutboxEntry,
): Promise<void> {
  if (link.kind === 'wo_task_completion_photo') {
    // WO completion photos: RPC also writes a wo_completion_photo_uploads
    // provenance row (who / when) so the Photos tab chip + Activity
    // timeline show a real upload timestamp.
    const rpcPromise = supabase.rpc('crew_add_task_completion_photos', {
      p_task_id: link.taskId,
      p_uploads: [
        {
          url: publicUrl,
          storage_path: entry.storagePath,
          file_name: link.fileName,
          content_type: link.contentType,
          byte_size: link.byteSize,
        },
      ],
    })
    const { error: rpcErr } = await withTimeout(
      Promise.resolve(rpcPromise).then((r) => r),
      LINK_TIMEOUT_MS,
      'photo-link-rpc',
    )
    if (rpcErr) throw rpcErr
    return
  }

  // Default: append URL into a text[] column on the target row (idempotent).
  const fetchPromise = supabase
    .from(link.table)
    .select(link.field)
    .eq('id', link.rowId)
    .single()
  const { data: row, error: fetchErr } = await withTimeout(
    Promise.resolve(fetchPromise).then((r) => r),
    LINK_TIMEOUT_MS,
    'photo-link-fetch',
  )
  if (fetchErr) throw fetchErr

  const existing = ((row as unknown as Record<string, unknown> | null)?.[link.field] as string[] | null) ?? []
  if (existing.includes(publicUrl)) return

  const updated = [...existing, publicUrl]
  const updatePayload: Record<string, unknown> = {
    ...(link.extraSet ?? {}),
    [link.field]: updated,
    updated_at: new Date().toISOString(),
  }
  const updatePromise = supabase
    .from(link.table)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update(updatePayload as any)
    .eq('id', link.rowId)
  const { error: updErr } = await withTimeout(
    Promise.resolve(updatePromise).then((r) => r),
    LINK_TIMEOUT_MS,
    'photo-link-update',
  )
  if (updErr) throw updErr
}

/**
 * Flush all pending entries, oldest first. Serial so we don't race two
 * updates against the same row's text[] column (last-write-wins would drop
 * one). Stops on the first entry that fails a network/auth check so we
 * don't burn attempts while offline.
 */
export async function flushAll(): Promise<{ sent: number; remaining: number; failed: number }> {
  let sent = 0
  let failed = 0
  const entries = (await listPendingPhotos()).reverse() // oldest first
  for (const entry of entries) {
    const result = await runEntry(entry)
    if (result.ok) {
      sent += 1
      continue
    }
    // Give up on this pass when it's a transient error — try again on the
    // next online/focus event rather than burning through the queue.
    const err = { message: result.error }
    if (!isOnline() || isNetworkError(err) || isAuthError(err)) {
      failed += 1
      break
    }
    failed += 1
    // Non-transient error (RLS, bad column, etc.) — keep trying the rest;
    // this bad entry can be resolved from the queued-photos UI.
  }
  const remaining = await pendingPhotoCount()
  return { sent, remaining, failed }
}

/**
 * Best-effort iOS/Android "Save to Photos" via the native Share sheet.
 * Falls back to a synthetic download link where Web Share files aren't
 * supported. Never throws — the caller doesn't need to try/catch.
 */
export async function savePhotoToDevice(file: File | Blob, fileName: string): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      const shareFile = file instanceof File ? file : new File([file], fileName, { type: (file as Blob).type || 'image/jpeg' })
      const canShare = (navigator as Navigator & { canShare?: (d: ShareData) => boolean }).canShare
      const data: ShareData = { files: [shareFile] }
      if (!canShare || canShare(data)) {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share(data)
        return
      }
    }
  } catch (err) {
    // User cancelled the share sheet, or platform doesn't like it — fall
    // through to the download fallback. Cancel is not an error state.
    if (err && (err as { name?: string }).name === 'AbortError') return
    console.warn('Web Share failed, falling back to download', err)
  }
  try {
    const url = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 5_000)
  } catch (err) {
    console.warn('Photo download fallback failed', err)
  }
}
